import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  resourceCalendarApi,
  type ActivityCode,
  type CalendarQuery,
  type CreateActivityPayload,
  type MovePayload,
} from '@/api/resourceCalendarApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * My Tasks (z8nrz7fh6h). Every key carries `selectedTenantId`, so a tenant
 * switch lands on a cold key; every write invalidates the whole calendar
 * family, because one move changes two cells, two loads and the queue.
 */
const ROOT = 'resource-calendar'

/** `enabled: false` holds the request, e.g. for someone who may not read the calendar. */
export function useResourceCalendar(query: CalendarQuery, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [ROOT, tenantId, 'calendar', query.from ?? null, query.to ?? null, query.teamId ?? null],
    queryFn: () => resourceCalendarApi.calendar(query),
    enabled: !!tenantId && enabled,
    // Shared by every supervisor planning the same days.
    staleTime: 30_000,
  })
}

/**
 * The task queue: every kind of pending work. A supervisor gets everyone's;
 * anyone else only what is assigned to them — the server decides.
 */
export function useTaskQueue(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [ROOT, tenantId, 'pending', 'ALL'],
    queryFn: () => resourceCalendarApi.pending(),
    enabled: !!tenantId && enabled,
    staleTime: 30_000,
  })
}

/** What is waiting to be planned, for one type. */
export function usePendingTasks(type: ActivityCode | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [ROOT, tenantId, 'pending', type],
    queryFn: () => resourceCalendarApi.pending(type as ActivityCode),
    enabled: !!tenantId && !!type && type !== 'UA',
    staleTime: 15_000,
  })
}

export function useCalendarMutations() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: [ROOT, tenantId] })
  return {
    /** Refetch after a write made elsewhere, e.g. a Resource Center absence. */
    refresh,
    create: useMutation({
      mutationFn: (body: CreateActivityPayload) => resourceCalendarApi.create(body),
      onSuccess: refresh,
    }),
    move: useMutation({
      mutationFn: (args: { key: string; body: MovePayload & { reason?: string } }) =>
        resourceCalendarApi.move(args.key, args.body),
      // A refused move (409: someone else moved it) also refetches, so the
      // calendar shows where the card really is now.
      onSettled: refresh,
    }),
    /** Not a write: what a move would hit, shown before it is confirmed. */
    preview: (key: string, body: MovePayload) => resourceCalendarApi.preview(key, body),
  }
}
